import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { Doughnut } from "react-chartjs-2";
import { MoreVertical } from "react-feather";

import { orange, green, red } from "@mui/material/colors";
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow as MuiTableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);

const ChartWrapper = styled.div`
  height: 168px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const TableRow = styled(MuiTableRow)`
  height: 42px;
`;

const TableCell = styled(MuiTableCell)`
  padding-top: 0;
  padding-bottom: 0;
`;

const GreenText = styled.span`
  color: ${() => green[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const RedText = styled.span`
  color: ${() => red[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const DoughnutChart = ({ theme, bookingStats = null }) => {
  // Process real booking statistics
  const confirmed = bookingStats?.confirmedBookings || 15;
  const pending = bookingStats?.pendingBookings || 5;
  const canceled = bookingStats?.canceledBookings || 3;
  const rejected = bookingStats?.rejectedBookings || 1;
  const total = confirmed + pending + canceled + rejected;

  const data = {
    labels: ["Confirmed", "Pending", "Canceled", "Rejected"],
    datasets: [
      {
        data: [confirmed, pending, canceled, rejected],
        backgroundColor: [
          green[500],
          orange[500],
          red[500],
          theme.palette.grey[400],
        ],
        borderWidth: 5,
        borderColor: theme.palette.background.paper,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    cutout: "80%",
  };

  const successRate = total > 0 ? ((confirmed / total) * 100).toFixed(1) : 0;

  return (
    <Card mb={6}>
      <CardHeader
        action={
          <IconButton aria-label="settings" size="large">
            <MoreVertical />
          </IconButton>
        }
        title="Booking Status"
      />

      <CardContent>
        <ChartWrapper>
          <DoughnutInner>
            <Typography variant="h4">{successRate}%</Typography>
            <Typography variant="caption">success rate</Typography>
          </DoughnutInner>
          <Doughnut data={data} options={options} />
        </ChartWrapper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Confirmed
              </TableCell>
              <TableCell align="right">{confirmed}</TableCell>
              <TableCell align="right">
                <GreenText>
                  {total > 0 ? ((confirmed / total) * 100).toFixed(1) : 0}%
                </GreenText>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Pending
              </TableCell>
              <TableCell align="right">{pending}</TableCell>
              <TableCell align="right">
                <span style={{ color: orange[500] }}>
                  {total > 0 ? ((pending / total) * 100).toFixed(1) : 0}%
                </span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Canceled
              </TableCell>
              <TableCell align="right">{canceled}</TableCell>
              <TableCell align="right">
                <RedText>
                  {total > 0 ? ((canceled / total) * 100).toFixed(1) : 0}%
                </RedText>
              </TableCell>
            </TableRow>
            {rejected > 0 && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Rejected
                </TableCell>
                <TableCell align="right">{rejected}</TableCell>
                <TableCell align="right">
                  <RedText>
                    {total > 0 ? ((rejected / total) * 100).toFixed(1) : 0}%
                  </RedText>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default withTheme(DoughnutChart);
